'use client';

import { useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import { AuthProvider, useAuth } from '@/lib/auth';
import type { SessionAuth } from '@/lib/training/api';
import {
  DashboardApiError,
  fetchDashboard,
  type DashboardResponse,
  type RankKey,
  type ReadinessBand,
} from '@/lib/dashboard/api';
import { useTrackSelection } from '@/lib/trackSelection/useTrackSelection';
import type { TrackSelection } from '@/lib/trackSelection/storage';
import RedirectToCatalog from '../_shared/RedirectToCatalog';
import EmptyState from '../_shared/EmptyState';
import ProjectionCard from './ProjectionCard';
import GoalEditor from './GoalEditor';
import '../_shared/shared.css';
import './home.css';
import ConsoleNav from '../_shared/ConsoleNav';

/**
 * World-flavored strings are resolved server-side (build time) by page.tsx and
 * passed in as props, so this client component never imports the fs-backed
 * vocabulary loader and the theming seam (K14) stays intact.
 */
export interface HomeStrings {
  route: string;
  level: string;
  streak: string;
  boss: string;
  mock: string;
  xp: string;
  mentor: string;
}

export default function HomeScreen({
  strings,
  levelNames,
  rankNames,
  bandNames,
}: {
  strings: HomeStrings;
  levelNames: Record<string, string>;
  rankNames: Record<RankKey, string>;
  bandNames: Record<ReadinessBand, string>;
}) {
  const trackSelection = useTrackSelection();

  if (trackSelection.status === 'checking') {
    return (
      <div className="route-loading" role="status" aria-live="polite" aria-busy="true">
        <span className="spinner" aria-hidden="true" />
        <span className="sr-only">Loading</span>
      </div>
    );
  }

  // Signing in does not need a track; seeing a dashboard does. This used to send anyone without a
  // selection straight to the catalog, which put the only "Continue with Google" in the product
  // behind picking a track first — so a returning user on a new device, whose selection lives in
  // this browser's storage and therefore does not exist, had no way back into their account at all
  // (2026-08-26). The dashboard below still requires a selection, and asks for one when signed in.
  if (trackSelection.status === 'none') {
    return (
      <AuthProvider>
        <SignedOutOrPickTrack />
      </AuthProvider>
    );
  }

  return (
    <AuthProvider>
      <HomeBridge
        strings={strings}
        levelNames={levelNames}
        rankNames={rankNames}
        bandNames={bandNames}
        selection={trackSelection.selection}
      />
    </AuthProvider>
  );
}

/** The slice of `useAuth()` this screen needs — narrowed so the fetch logic below is testable without a real `AuthProvider`. */
export interface HomeAuth {
  status: 'loading' | 'guest' | 'authed';
  getSessionAuth: () => Promise<SessionAuth>;
  beginGoogleSignIn: () => void;
}

/**
 * What a visitor with no track selected sees on /home.
 *
 * A guest is offered the account, because that is what brought them here and it needs no track. A
 * signed-in user is sent to pick one, because their dashboard is per-track and there is nothing to
 * show until they have. Both beat the old behaviour of bouncing everyone to the catalog, which hid
 * sign-in from the people looking for it.
 */
function SignedOutOrPickTrack() {
  return <SignedOutOrPickTrackBody auth={useAuth()} />;
}

/** Split from the wrapper above so a test can drive it without an AuthProvider, exactly as
 *  HomeScreenBody is. */
export function SignedOutOrPickTrackBody({ auth }: { auth: HomeAuth }) {

  if (auth.status === 'loading') {
    return (
      <div className="route-loading" role="status" aria-live="polite" aria-busy="true">
        <span className="spinner" aria-hidden="true" />
        <span className="sr-only">Loading</span>
      </div>
    );
  }

  if (auth.status === 'authed') return <RedirectToCatalog />;

  return (
    <EmptyState
      title="Sign in to SIMBAY"
      message="Pick up where you left off, on any device: your readiness, streak and rank travel with the account."
      action={
        <div className="btn-row">
          <button type="button" className="btn btn-primary" onClick={() => auth.beginGoogleSignIn()}>
            Continue with Google
          </button>
          <Link href="/catalog" className="btn btn-secondary">
            Browse tracks
          </Link>
        </div>
      }
    />
  );
}

function HomeBridge({
  strings,
  levelNames,
  rankNames,
  bandNames,
  selection,
}: {
  strings: HomeStrings;
  levelNames: Record<string, string>;
  rankNames: Record<RankKey, string>;
  bandNames: Record<ReadinessBand, string>;
  selection: TrackSelection;
}) {
  const auth = useAuth();
  return (
    <HomeScreenBody
      strings={strings}
      levelNames={levelNames}
      rankNames={rankNames}
      bandNames={bandNames}
      auth={auth}
      selection={selection}
    />
  );
}

function describeDashboardError(err: unknown): string {
  if (err instanceof DashboardApiError) {
    return `Could not reach the dashboard service (${err.status}). Try again.`;
  }
  return 'Could not load your dashboard. Try again in a moment.';
}

/** Fetches + renders the caller's dashboard. Exported for direct testing against a fake `auth`. */
export function HomeScreenBody({
  strings,
  levelNames,
  rankNames,
  bandNames,
  auth,
  selection,
}: {
  strings: HomeStrings;
  levelNames: Record<string, string>;
  rankNames: Record<RankKey, string>;
  bandNames: Record<ReadinessBand, string>;
  auth: HomeAuth;
  selection: TrackSelection;
}) {
  const [fetchStatus, setFetchStatus] = useState<'pending' | 'ready' | 'error'>('pending');
  const [dashboard, setDashboard] = useState<DashboardResponse | null>(null);
  const [authedSession, setAuthedSession] = useState<{ idToken: string; identityId: string } | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setFetchStatus('pending');
    setError(null);
    try {
      const sessionAuth = await auth.getSessionAuth();
      if (sessionAuth.kind !== 'authed') {
        // The dashboard is JWT-only; guests never reach this branch since
        // the effect below only fires once `auth.status === 'authed'`.
        throw new Error('Dashboard requires a signed-in session.');
      }
      const result = await fetchDashboard(sessionAuth.idToken, sessionAuth.identityId, {
        ecosystem: selection.ecosystem,
        route: selection.route,
      });
      setDashboard(result);
      setAuthedSession({ idToken: sessionAuth.idToken, identityId: sessionAuth.identityId });
      setFetchStatus('ready');
    } catch (err) {
      setError(describeDashboardError(err));
      setFetchStatus('error');
    }
  }, [auth, selection]);

  // Intentionally keyed on `auth.status` only (mirrors MapConsoleBody) — the
  // dashboard should fetch once the auth tier resolves to authed, not
  // re-fetch on every unrelated auth update.
  useEffect(() => {
    if (auth.status !== 'authed') return;
    void load();
  }, [auth.status]);

  if (auth.status === 'loading') {
    return (
      <div className="route-loading" role="status" aria-live="polite" aria-busy="true">
        <span className="spinner" aria-hidden="true" />
        <span className="sr-only">Loading dashboard</span>
      </div>
    );
  }

  if (auth.status === 'guest') {
    return (
      <EmptyState
        title="See your progress"
        message="Create an account to see your dashboard: readiness, streak, and rank all in one place."
        action={
          <div className="btn-row">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => auth.beginGoogleSignIn()}
            >
              Continue with Google
            </button>
            <Link href="/train" className="btn btn-secondary">
              Start training as a guest
            </Link>
          </div>
        }
      />
    );
  }

  if (fetchStatus === 'pending') {
    return (
      <div className="route-loading" role="status" aria-live="polite" aria-busy="true">
        <span className="spinner" aria-hidden="true" />
        <span className="sr-only">Loading dashboard</span>
      </div>
    );
  }

  if (fetchStatus === 'error' || !dashboard || !authedSession) {
    return (
      <EmptyState
        title="Dashboard unavailable"
        message={error ?? 'Could not load your dashboard. Try again in a moment.'}
        action={
          <button type="button" className="btn btn-primary" onClick={() => void load()}>
            Retry
          </button>
        }
      />
    );
  }

  return (
    <DashboardView
      dashboard={dashboard}
      strings={strings}
      levelNames={levelNames}
      rankNames={rankNames}
      bandNames={bandNames}
      sessionAuth={authedSession}
      onGoalSaved={load}
    />
  );
}

function DashboardView({
  dashboard,
  strings,
  levelNames,
  rankNames,
  bandNames,
  sessionAuth,
  onGoalSaved,
}: {
  dashboard: DashboardResponse;
  strings: HomeStrings;
  levelNames: Record<string, string>;
  rankNames: Record<RankKey, string>;
  bandNames: Record<ReadinessBand, string>;
  sessionAuth: { idToken: string; identityId: string };
  onGoalSaved: () => void;
}) {
  return (
    <main className="home-console">
      <header className="console-topbar">
        <span className="console-title">SIMBAY // DASHBOARD</span>
        <ConsoleNav />
      </header>
      <div className="home-grid">
        <ExamCard exam={dashboard.exam} />
        <StreakCard streak={dashboard.streak} strings={strings} />
        <RankCard rank={dashboard.rank} strings={strings} rankNames={rankNames} />
        <ReadinessCard readiness={dashboard.readiness} bandNames={bandNames} />
        <ProjectionCard projection={dashboard.pace_projection} />
        <NextActionCard
          nextAction={dashboard.next_action}
          strings={strings}
          levelNames={levelNames}
        />
      </div>
      <GoalEditor dashboard={dashboard} sessionAuth={sessionAuth} onSaved={onGoalSaved} />
    </main>
  );
}

function ExamCard({ exam }: { exam: DashboardResponse['exam'] }) {
  if (exam.exam_date === null || exam.days_remaining === null) return null;
  const label =
    exam.days_remaining < 0
      ? 'Exam date passed'
      : exam.days_remaining === 0
        ? 'Exam day'
        : `T-minus ${exam.days_remaining} day${exam.days_remaining === 1 ? '' : 's'}`;
  return (
    <section className="home-card" data-testid="exam-card">
      <h2 className="home-card-title">T-minus</h2>
      <p className="home-card-metric">{label}</p>
    </section>
  );
}

function StreakCard({
  streak,
  strings,
}: {
  streak: DashboardResponse['streak'];
  strings: HomeStrings;
}) {
  return (
    <section className="home-card" data-testid="streak-card">
      <h2 className="home-card-title">{strings.streak}</h2>
      <p className="home-card-metric">
        {streak.current_streak} day{streak.current_streak === 1 ? '' : 's'}
      </p>
      <dl className="home-card-stats">
        <dt>Longest</dt>
        <dd>{streak.longest_streak}</dd>
        <dt>Freezes available</dt>
        <dd>{streak.freezes_available}</dd>
      </dl>
    </section>
  );
}

function RankCard({
  rank,
  strings,
  rankNames,
}: {
  rank: DashboardResponse['rank'];
  strings: HomeStrings;
  rankNames: Record<RankKey, string>;
}) {
  const span = rank.xp_to_next_rank === null ? null : rank.xp_into_rank + rank.xp_to_next_rank;
  const progressPct = span === null || span === 0 ? 100 : Math.min(100, Math.round((rank.xp_into_rank / span) * 100));
  return (
    <section className="home-card" data-testid="rank-card">
      <h2 className="home-card-title">Rank</h2>
      <p className="home-card-metric">{rankNames[rank.rank]}</p>
      <div
        className="home-progress-track"
        role="progressbar"
        aria-label="Progress to next rank"
        aria-valuenow={progressPct}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div className="home-progress-fill" style={{ width: `${progressPct}%` }} />
      </div>
      <p className="home-card-sub">
        {rank.next_rank
          ? `${rank.xp_to_next_rank} ${strings.xp} to ${rankNames[rank.next_rank]}`
          : 'Top rank reached'}
      </p>
      <p className="home-card-sub">
        {rank.total_xp} total {strings.xp}
      </p>
    </section>
  );
}

function ReadinessCard({
  readiness,
  bandNames,
}: {
  readiness: DashboardResponse['readiness'];
  bandNames: Record<ReadinessBand, string>;
}) {
  return (
    <section className="home-card" data-testid="readiness-card">
      <h2 className="home-card-title">Readiness</h2>
      <div className="home-readiness-head">
        <span className="home-readiness-pill" data-band={readiness.band}>
          {bandNames[readiness.band]}
        </span>
        <span className="home-card-metric">{readiness.score}</span>
      </div>
      {!readiness.has_mock_history && (
        <p className="home-card-sub" data-testid="readiness-capped-note">
          Capped until you complete a mock.
        </p>
      )}
      {readiness.domains.length > 0 && (
        <ul className="home-domain-list" role="list">
          {readiness.domains.map((domain) => (
            <li key={domain.domain_key} className="home-domain-row">
              <span className="home-domain-key">{domain.domain_key}</span>
              <div
                className="home-progress-track"
                role="progressbar"
                aria-label={`${domain.domain_key} readiness`}
                aria-valuenow={Math.round(domain.score * 100)}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <div
                  className="home-progress-fill"
                  style={{ width: `${Math.round(Math.min(1, Math.max(0, domain.score)) * 100)}%` }}
                />
              </div>
              <span className="home-domain-score">{Math.round(domain.score * 100)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function NextActionCard({
  nextAction,
  strings,
  levelNames,
}: {
  nextAction: DashboardResponse['next_action'];
  strings: HomeStrings;
  levelNames: Record<string, string>;
}) {
  const title =
    nextAction.type === 'review'
      ? 'Review due cards'
      : nextAction.type === 'continue'
        ? `Continue ${levelNames[nextAction.level_key] ?? nextAction.level_key}`
        : `Take on a ${strings.boss} or ${strings.mock}`;
  const cta = nextAction.type === 'review' ? 'Review' : 'Continue';

  return (
    <section className="home-card home-card-action" data-testid="next-action-card">
      <h2 className="home-card-title">Next up</h2>
      <p className="home-card-metric">{title}</p>
      <Link href="/train" className="btn btn-primary">
        {cta}
      </Link>
    </section>
  );
}
